import type { ReactNode } from 'react';
import type { BoardLayout } from '../../../lib/team-meeting-board.js';
import type { CategoryColumn } from '../../../lib/team-meetings.js';

/** 手書きの枠の線・文字の色（FigJam の枠の色）。 @graph-connects none */
const INK = '#b4c6de';

/** 区分の見出しの手書き風の書体。 @graph-connects none */
const HAND_FONT = "'Yomogi', 'Klee One', cursive";

/** 区分の置き場 1 つの中身を描く口（付箋と「付箋を貼る」）。 @graph-connects none */
type RenderZone = (column: CategoryColumn) => ReactNode;

/**
 * 区分の置き場 1 つ（手書き風の見出しと付箋）。
 * @graph-connects none
 */
function Zone({
  column,
  render,
  isTitle = false,
  decoration = null,
  className = '',
}: {
  column: CategoryColumn;
  render: RenderZone;
  /** 中央の柱の見出し（手書きではなく太字の題にする） */
  isTitle?: boolean;
  /** 置き場の縁に重ねる飾り（顔） */
  decoration?: ReactNode;
  className?: string;
}): ReactNode {
  const { category, posts } = column;
  return (
    <section className={`relative z-10 p-4 ${className}`} aria-label={category.label}>
      {decoration}
      <h3
        className={
          isTitle
            ? 'mb-3 text-center text-base font-bold text-[#17a398]'
            : 'mb-3 text-2xl leading-tight lg:text-[28px]'
        }
        style={isTitle ? undefined : { color: INK, fontFamily: HAND_FONT }}
      >
        {category.label}
        <span className="ml-2 align-middle font-sans text-xs text-[#8aa0bf]">{posts.length}</span>
      </h3>
      <div className="grid grid-cols-2 items-start gap-3 sm:grid-cols-[repeat(auto-fill,minmax(160px,1fr))]">
        {render(column)}
      </div>
    </section>
  );
}

/**
 * 顔（にっこり・しょんぼり）。枠の線の上に置く飾り。
 * @graph-connects none
 */
function Face({ mood, className }: { mood: 'happy' | 'sad'; className: string }): ReactNode {
  return (
    <svg
      viewBox="0 0 60 60"
      className={`pointer-events-none absolute hidden h-14 w-14 lg:block ${className}`}
      aria-hidden="true"
    >
      <circle cx="30" cy="30" r="26" fill="#fcfbf7" stroke={INK} strokeWidth="4" />
      <circle cx="22" cy="24" r="2.5" fill={INK} />
      <circle cx="38" cy="24" r="2.5" fill={INK} />
      <path
        d={mood === 'happy' ? 'M19 35 Q30 46 41 35' : 'M20 42 Q30 31 40 42'}
        fill="none"
        stroke={INK}
        strokeWidth="4"
        strokeLinecap="round"
      />
    </svg>
  );
}

/**
 * 左半分の枠の線（中央から左上・左下へ開く 2 本の斜線）。
 * @graph-connects none
 */
function LeftLines(): ReactNode {
  return (
    <svg
      className="pointer-events-none absolute inset-0 hidden h-full w-full lg:block"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      {[18, 82].map((y) => (
        <line
          key={y}
          x1="100"
          y1="50"
          x2="6"
          y2={y}
          stroke={INK}
          strokeWidth="5"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      ))}
    </svg>
  );
}

/**
 * 盤面を回る薄い赤い矢印（FigJam の枠の飾り）。
 * @graph-connects none
 */
function CycleArrow(): ReactNode {
  return (
    <div
      className="pointer-events-none absolute left-[24%] top-[16%] z-0 hidden h-[66%] w-[52%] lg:block"
      aria-hidden="true"
    >
      <div className="h-full w-full rounded-[50%] border-[14px] border-[rgba(240,120,120,0.13)] border-t-transparent" />
      <div className="absolute left-[16%] top-[9%] h-10 w-10 rotate-[20deg] border-r-[14px] border-t-[14px] border-[rgba(240,120,120,0.13)]" />
    </div>
  );
}

/**
 * 週次MTG のボード（FigJam の枠を写した見た目）。左半分に良かったこと（お礼・ドヤ・続けたい）、
 * 右半分に聞いて欲しい・変えたい、中央の柱に AI Sharing を置き、枠に無い区分は下に並べる。
 * 大きい画面ではボードを 2800×1200px に固定する（拡大・縮小は {@link BoardViewport} が掛ける）。FigJam と
 * 同じく、付箋が無い回でも左の区分 1 つに 10 枚ほど（5 枚 × 2 段）貼れる広さを取る。付箋が一番多い
 * 「聞いて欲しい」（右半分の上）は、右半分を広く取ったうえで高さの 2/3 を充てる。狭い画面では区分を縦に積み、枠の線・顔・矢印は出さない。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 週次MTG のボードを FigJam の枠と同じ配置（左に良かったこと・右に聞いて欲しい／変えたい・中央に AI Sharing）で描き、各区分に付箋を並べる
 * @graph-connects none
 */
export function BoardCanvas({
  layout,
  renderZone,
}: {
  layout: BoardLayout;
  renderZone: RenderZone;
}): ReactNode {
  const { left, right, center, extra } = layout;
  return (
    <div className="space-y-3 lg:w-[2800px]">
      <div className="relative overflow-hidden rounded-lg border border-[#e4e2dc] bg-[#fcfbf7] shadow-sm lg:grid lg:min-h-[1200px] lg:grid-cols-[minmax(0,1fr)_300px_minmax(0,1.6fr)]">
        <CycleArrow />
        <div className="relative flex flex-col">
          <LeftLines />
          <Face mood="happy" className="-right-1 top-1/2 z-10 -translate-y-1/2" />
          {left.map((column) => (
            <Zone key={column.category.id} column={column} render={renderZone} className="flex-1" />
          ))}
        </div>

        <div className="relative flex justify-center py-6">
          <span
            className="absolute inset-y-4 left-1/2 hidden w-[6px] -translate-x-1/2 rounded-full lg:block"
            style={{ backgroundColor: INK }}
            aria-hidden="true"
          />
          {center !== null && (
            <div className="relative z-10 w-full self-center rounded-sm border border-[#e3e8ef] bg-[#f1f4f8] lg:mx-3 lg:min-h-[60%]">
              <Zone column={center} render={renderZone} isTitle />
            </div>
          )}
        </div>

        <div className="relative flex flex-col">
          {right.map((column, i) => (
            <Zone
              key={column.category.id}
              column={column}
              render={renderZone}
              className={i > 0 ? 'flex-1 lg:border-t-[5px] lg:border-[#b4c6de]' : 'flex-[2]'}
              decoration={
                i > 0 && <Face mood="sad" className="left-[18%] top-0 -translate-y-1/2" />
              }
            />
          ))}
        </div>
      </div>

      {extra.length > 0 && (
        <div className="grid gap-3 rounded-lg border border-[#e4e2dc] bg-[#fcfbf7] md:grid-cols-2">
          {extra.map((column) => (
            <Zone key={column.category.id} column={column} render={renderZone} />
          ))}
        </div>
      )}
    </div>
  );
}
